<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuth0 } from '@auth0/auth0-vue'
import { useUserStore } from '../stores/user'
import { trackEvent } from '../services/analytics'
import {
    signupAdvisor,
    claimSupplier,
    validateClaimCode,
    validateAdvisorInviteCode
} from '../services/api'

const router = useRouter()
const route = useRoute()
const auth0 = useAuth0()
const { user: auth0User, getAccessTokenSilently, isAuthenticated } = auth0
const userStore = useUserStore()

// Flow state
type Step = 'choose-path' | 'code-input' | 'details' | 'complete'
type PathChoice = 'supplier-claim' | 'advisor-invite' | 'waitlist' | null

const step = ref<Step>('choose-path')
const selectedPath = ref<PathChoice>(null)
const isLoading = ref(false)
const error = ref<string | null>(null)

// Code validation
const codeInput = ref('')
const isValidatingCode = ref(false)
const codeValidated = ref(false)

// Supplier claim data (populated after code validation)
const validatedSupplier = ref<{ id: string; name: string } | null>(null)

// Advisor invite form
const advisorForm = ref({
    firmName: '',
    region: '',
    tier: 'FREE' as 'FREE' | 'PROFESSIONAL'
})

// Waitlist form
const waitlistEmail = ref('')
const waitlistSubmitted = ref(false)

const tierDescriptions = {
    FREE: 'Supplier Directory, 10 AI queries/month',
    PROFESSIONAL: 'Full access to all features - $40/mo or $350/yr (45-day free trial)'
}

// Check for pre-filled codes from URL — auto-validate and advance past code step
onMounted(async () => {
    const claimCode = route.query.claim as string
    const inviteCode = route.query.invite as string

    if (claimCode) {
        selectedPath.value = 'supplier-claim'
        codeInput.value = claimCode
        step.value = 'code-input'
        await validateCode()
        // Auto-advance if validation succeeded (user already validated on login page)
        if (codeValidated.value) {
            step.value = 'details'
        }
    } else if (inviteCode) {
        selectedPath.value = 'advisor-invite'
        codeInput.value = inviteCode
        step.value = 'code-input'
        await validateCode()
        if (codeValidated.value) {
            step.value = 'details'
        }
    }
})

function selectPath(path: PathChoice) {
    selectedPath.value = path
    error.value = null
    codeInput.value = ''
    codeValidated.value = false
    validatedSupplier.value = null

    // Track signup path selection
    trackEvent('signup_started', {
        role_selected: path === 'supplier-claim' ? 'SUPPLIER_ADMIN' : path === 'advisor-invite' ? 'ADVISOR' : 'waitlist',
    })

    if (path === 'waitlist') {
        // Waitlist doesn't need a code step
        step.value = 'code-input'
    } else {
        step.value = 'code-input'
    }
}

function goBack() {
    if (step.value === 'details') {
        step.value = 'code-input'
    } else if (step.value === 'code-input') {
        step.value = 'choose-path'
        selectedPath.value = null
        codeInput.value = ''
        codeValidated.value = false
        validatedSupplier.value = null
        error.value = null
        waitlistSubmitted.value = false
    }
}

async function validateCode() {
    if (!codeInput.value.trim()) return

    isValidatingCode.value = true
    error.value = null
    codeValidated.value = false
    validatedSupplier.value = null

    try {
        if (selectedPath.value === 'supplier-claim') {
            const result = await validateClaimCode(codeInput.value.trim())
            if (result.valid && result.supplier) {
                codeValidated.value = true
                validatedSupplier.value = result.supplier
            } else {
                error.value = result.error || 'Invalid claim code'
            }
        } else if (selectedPath.value === 'advisor-invite') {
            const result = await validateAdvisorInviteCode(codeInput.value.trim())
            if (result.valid) {
                codeValidated.value = true
            } else {
                error.value = result.error || 'Invalid invite code'
            }
        }
    } catch (e: unknown) {
        error.value = e instanceof Error ? e.message : 'Failed to validate code'
    } finally {
        isValidatingCode.value = false
    }
}

function proceedToDetails() {
    if (!codeValidated.value) return
    error.value = null
    step.value = 'details'
}

function submitWaitlist() {
    // Stub - just show confirmation
    waitlistSubmitted.value = true
}

async function submitSignup() {
    if (!isAuthenticated.value || !auth0User.value) {
        error.value = 'Please log in first'
        return
    }

    isLoading.value = true
    error.value = null

    try {
        const token = await getAccessTokenSilently()
        const email = auth0User.value.email!
        const name = auth0User.value.name || null

        let response

        if (selectedPath.value === 'supplier-claim' && validatedSupplier.value) {
            response = await claimSupplier({
                email,
                name,
                claimCode: codeInput.value.trim()
            }, token)
        } else if (selectedPath.value === 'advisor-invite') {
            response = await signupAdvisor({
                email,
                name,
                firmName: advisorForm.value.firmName || undefined,
                region: advisorForm.value.region || undefined,
                tier: advisorForm.value.tier,
                inviteCode: codeInput.value.trim()
            }, token)
        } else {
            error.value = 'Invalid signup path'
            return
        }

        // Track successful signup
        trackEvent('signup_completed', {
            role: response.user.role,
            method: selectedPath.value === 'supplier-claim' ? 'claim' : 'invite',
        })

        // Update user store and redirect to onboarding
        userStore.setUser(response.user)
        router.push(userStore.getOnboardingRoute())
    } catch (e: unknown) {
        if (e && typeof e === 'object' && 'response' in e) {
            const axiosErr = e as import('axios').AxiosError<{ error?: string }>
            error.value = axiosErr.response?.data?.error || axiosErr.message || 'Signup failed'
        } else {
            error.value = e instanceof Error ? e.message : 'Signup failed'
        }
    } finally {
        isLoading.value = false
    }
}
</script>

<template>
    <div class="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div class="bg-cc-elevated rounded-2xl shadow-xl max-w-lg w-full p-8">
            <!-- Header -->
            <div class="text-center mb-8">
                <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
                    Welcome to ChannelStandard
                </h1>
                <p class="text-slate-500 dark:text-gray-400 mt-2">
                    <template v-if="step === 'choose-path'">Get started by choosing your path</template>
                    <template v-else-if="step === 'code-input' && selectedPath === 'supplier-claim'">Enter your supplier claim code</template>
                    <template v-else-if="step === 'code-input' && selectedPath === 'advisor-invite'">Enter your advisor invite code</template>
                    <template v-else-if="step === 'code-input' && selectedPath === 'waitlist'">Join our waitlist</template>
                    <template v-else-if="step === 'details' && selectedPath === 'supplier-claim'">Confirm your company</template>
                    <template v-else-if="step === 'details' && selectedPath === 'advisor-invite'">Complete your advisor profile</template>
                </p>
            </div>

            <!-- Error Message -->
            <div v-if="error" class="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-400 text-sm">
                {{ error }}
            </div>

            <!-- ========================================== -->
            <!-- Step 1: Choose Path                        -->
            <!-- ========================================== -->
            <div v-if="step === 'choose-path'" class="space-y-4">
                <!-- Supplier claim is no longer offered as a visible path; the
                     direct-link flow (/signup?claim=CODE) still works so claim
                     codes already in suppliers' hands keep functioning. -->

                <!-- Advisor Invite Code -->
                <button
                    @click="selectPath('advisor-invite')"
                    class="w-full p-6 border-2 border-slate-200 dark:border-gray-600 rounded-xl hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-all text-left group"
                >
                    <div class="flex items-start gap-4">
                        <div class="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center shrink-0">
                            <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                            </svg>
                        </div>
                        <div>
                            <h3 class="font-semibold text-slate-900 dark:text-white">I have an Advisor Invite Code</h3>
                            <p class="text-sm text-slate-500 dark:text-gray-400 mt-1">
                                Join as a technology advisor with your invite code
                            </p>
                        </div>
                    </div>
                </button>

                <!-- Waitlist -->
                <button
                    @click="selectPath('waitlist')"
                    class="w-full p-6 border-2 border-slate-200 dark:border-gray-600 rounded-xl hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-all text-left group"
                >
                    <div class="flex items-start gap-4">
                        <div class="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center shrink-0">
                            <svg class="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div>
                            <h3 class="font-semibold text-slate-900 dark:text-white">Join the Waitlist</h3>
                            <p class="text-sm text-slate-500 dark:text-gray-400 mt-1">
                                Don't have a code? Sign up to be notified when spots open
                            </p>
                        </div>
                    </div>
                </button>
            </div>

            <!-- ========================================== -->
            <!-- Step 2a: Code Input (Supplier Claim)       -->
            <!-- ========================================== -->
            <div v-if="step === 'code-input' && selectedPath === 'supplier-claim'" class="space-y-6">
                <div>
                    <label class="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Claim Code</label>
                    <div class="flex gap-2">
                        <input
                            v-model="codeInput"
                            type="text"
                            class="flex-1 px-4 py-2.5 border border-slate-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 uppercase placeholder:normal-case"
                            placeholder="CLAIM-XXXXXX"
                            @keyup.enter="validateCode"
                        />
                        <button
                            @click="validateCode"
                            :disabled="isValidatingCode || !codeInput.trim()"
                            class="px-5 py-2.5 bg-slate-100 dark:bg-gray-600 text-slate-700 dark:text-gray-200 rounded-xl hover:bg-slate-200 dark:hover:bg-gray-500 disabled:opacity-50 transition-colors"
                        >
                            {{ isValidatingCode ? 'Checking...' : 'Verify' }}
                        </button>
                    </div>
                </div>

                <!-- Validated supplier info -->
                <div v-if="codeValidated && validatedSupplier" class="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                    <div class="flex items-center gap-2">
                        <svg class="w-5 h-5 text-green-600 dark:text-green-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span class="text-green-800 dark:text-green-300 font-medium">Valid claim code</span>
                    </div>
                    <p class="text-green-700 dark:text-green-400 text-sm mt-1 ml-7">
                        You're claiming: <strong>{{ validatedSupplier.name }}</strong>
                    </p>
                </div>

                <div class="flex gap-3 pt-2">
                    <button
                        @click="goBack"
                        class="flex-1 px-4 py-2.5 border border-slate-300 dark:border-gray-600 rounded-xl text-slate-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        Back
                    </button>
                    <button
                        @click="proceedToDetails"
                        :disabled="!codeValidated"
                        class="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 transition-colors"
                    >
                        Continue
                    </button>
                </div>
            </div>

            <!-- ========================================== -->
            <!-- Step 2b: Code Input (Advisor Invite)       -->
            <!-- ========================================== -->
            <div v-if="step === 'code-input' && selectedPath === 'advisor-invite'" class="space-y-6">
                <div>
                    <label class="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Invite Code</label>
                    <div class="flex gap-2">
                        <input
                            v-model="codeInput"
                            type="text"
                            class="flex-1 px-4 py-2.5 border border-slate-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase placeholder:normal-case"
                            placeholder="INV-XXXXXX"
                            @keyup.enter="validateCode"
                        />
                        <button
                            @click="validateCode"
                            :disabled="isValidatingCode || !codeInput.trim()"
                            class="px-5 py-2.5 bg-slate-100 dark:bg-gray-600 text-slate-700 dark:text-gray-200 rounded-xl hover:bg-slate-200 dark:hover:bg-gray-500 disabled:opacity-50 transition-colors"
                        >
                            {{ isValidatingCode ? 'Checking...' : 'Verify' }}
                        </button>
                    </div>
                </div>

                <!-- Validated invite -->
                <div v-if="codeValidated" class="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                    <div class="flex items-center gap-2">
                        <svg class="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span class="text-blue-800 dark:text-blue-300 font-medium">Valid invite code</span>
                    </div>
                    <p class="text-blue-700 dark:text-blue-400 text-sm mt-1 ml-7">
                        You're all set to create your advisor account.
                    </p>
                </div>

                <div class="flex gap-3 pt-2">
                    <button
                        @click="goBack"
                        class="flex-1 px-4 py-2.5 border border-slate-300 dark:border-gray-600 rounded-xl text-slate-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        Back
                    </button>
                    <button
                        @click="proceedToDetails"
                        :disabled="!codeValidated"
                        class="flex-1 px-4 py-2.5 bg-cc-primary text-cc-btn-text rounded-xl hover:bg-cc-primary-hover disabled:opacity-50 transition-colors"
                    >
                        Continue
                    </button>
                </div>
            </div>

            <!-- ========================================== -->
            <!-- Step 2c: Waitlist (Stub)                   -->
            <!-- ========================================== -->
            <div v-if="step === 'code-input' && selectedPath === 'waitlist'" class="space-y-6">
                <template v-if="!waitlistSubmitted">
                    <div class="text-center py-2">
                        <div class="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg class="w-8 h-8 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <p class="text-slate-600 dark:text-gray-400 text-sm">
                            We're onboarding new users in waves. Leave your email and we'll notify you when your spot opens up.
                        </p>
                    </div>

                    <div>
                        <label class="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Email Address</label>
                        <input
                            v-model="waitlistEmail"
                            type="email"
                            class="w-full px-4 py-2.5 border border-slate-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-cc-primary focus:border-indigo-500"
                            placeholder="you@company.com"
                            @keyup.enter="submitWaitlist"
                        />
                    </div>

                    <div class="flex gap-3 pt-2">
                        <button
                            @click="goBack"
                            class="flex-1 px-4 py-2.5 border border-slate-300 dark:border-gray-600 rounded-xl text-slate-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            Back
                        </button>
                        <button
                            @click="submitWaitlist"
                            :disabled="!waitlistEmail.trim()"
                            class="flex-1 px-4 py-2.5 bg-cc-primary text-cc-btn-text rounded-xl hover:bg-cc-primary-hover disabled:opacity-50 transition-colors"
                        >
                            Join Waitlist
                        </button>
                    </div>
                </template>

                <template v-else>
                    <div class="text-center py-6">
                        <div class="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg class="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h3 class="text-lg font-semibold text-slate-900 dark:text-white mb-2">Thanks! We'll be in touch.</h3>
                        <p class="text-slate-500 dark:text-gray-400 text-sm">
                            We've added <strong>{{ waitlistEmail }}</strong> to our waitlist. You'll receive an invite code when a spot opens up.
                        </p>
                    </div>

                    <button
                        @click="goBack"
                        class="w-full px-4 py-2.5 border border-slate-300 dark:border-gray-600 rounded-xl text-slate-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        Back
                    </button>
                </template>
            </div>

            <!-- ========================================== -->
            <!-- Step 3a: Supplier Claim Confirmation       -->
            <!-- ========================================== -->
            <div v-if="step === 'details' && selectedPath === 'supplier-claim'" class="space-y-6">
                <div class="p-5 bg-slate-50 dark:bg-gray-700/50 border border-slate-200 dark:border-gray-600 rounded-xl">
                    <div class="flex items-center gap-3 mb-3">
                        <div class="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                            <svg class="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                        </div>
                        <div>
                            <p class="text-sm text-slate-500 dark:text-gray-400">Claiming company</p>
                            <p class="font-semibold text-slate-900 dark:text-white text-lg">{{ validatedSupplier?.name }}</p>
                        </div>
                    </div>
                    <p class="text-sm text-slate-500 dark:text-gray-400">
                        You will be assigned as the owner of this company profile. You can manage products, team members, and company settings once your account is active.
                    </p>
                </div>

                <div class="flex gap-3 pt-2">
                    <button
                        @click="goBack"
                        class="flex-1 px-4 py-2.5 border border-slate-300 dark:border-gray-600 rounded-xl text-slate-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        Back
                    </button>
                    <button
                        @click="submitSignup"
                        :disabled="isLoading"
                        class="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 transition-colors"
                    >
                        {{ isLoading ? 'Claiming...' : 'Claim Account' }}
                    </button>
                </div>
            </div>

            <!-- ========================================== -->
            <!-- Step 3b: Advisor Details                   -->
            <!-- ========================================== -->
            <div v-if="step === 'details' && selectedPath === 'advisor-invite'" class="space-y-6">
                <div>
                    <label class="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Firm Name <span class="text-slate-400 dark:text-gray-500 font-normal">(optional)</span></label>
                    <input
                        v-model="advisorForm.firmName"
                        type="text"
                        class="w-full px-4 py-2.5 border border-slate-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Your company or firm name"
                    />
                </div>

                <div>
                    <label class="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-2">Region <span class="text-slate-400 dark:text-gray-500 font-normal">(optional)</span></label>
                    <input
                        v-model="advisorForm.region"
                        type="text"
                        class="w-full px-4 py-2.5 border border-slate-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Northeast, West Coast"
                    />
                </div>

                <div>
                    <label class="block text-sm font-medium text-slate-700 dark:text-gray-300 mb-3">Choose your plan</label>
                    <div class="space-y-3">
                        <label
                            class="flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-colors"
                            :class="advisorForm.tier === 'FREE' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-gray-600 hover:border-slate-300 dark:hover:border-gray-500'"
                        >
                            <input type="radio" v-model="advisorForm.tier" value="FREE" class="mt-1" />
                            <div>
                                <div class="font-medium text-slate-900 dark:text-white">Free</div>
                                <div class="text-sm text-slate-500 dark:text-gray-400">{{ tierDescriptions.FREE }}</div>
                            </div>
                        </label>
                        <label
                            class="flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-colors"
                            :class="advisorForm.tier === 'PROFESSIONAL' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-gray-600 hover:border-slate-300 dark:hover:border-gray-500'"
                        >
                            <input type="radio" v-model="advisorForm.tier" value="PROFESSIONAL" class="mt-1" />
                            <div>
                                <div class="font-medium text-slate-900 dark:text-white">Professional</div>
                                <div class="text-sm text-slate-500 dark:text-gray-400">{{ tierDescriptions.PROFESSIONAL }}</div>
                            </div>
                        </label>
                    </div>
                </div>

                <div class="flex gap-3 pt-2">
                    <button
                        @click="goBack"
                        class="flex-1 px-4 py-2.5 border border-slate-300 dark:border-gray-600 rounded-xl text-slate-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        Back
                    </button>
                    <button
                        @click="submitSignup"
                        :disabled="isLoading"
                        class="flex-1 px-4 py-2.5 bg-cc-primary text-cc-btn-text rounded-xl hover:bg-cc-primary-hover disabled:opacity-50 transition-colors"
                    >
                        {{ isLoading ? 'Creating account...' : 'Create Account' }}
                    </button>
                </div>
            </div>

            <!-- Step progress indicator -->
            <div class="mt-8 flex items-center justify-center gap-2">
                <div
                    class="w-2 h-2 rounded-full transition-colors"
                    :class="step === 'choose-path' ? 'bg-slate-800 dark:bg-white' : 'bg-slate-300 dark:bg-gray-600'"
                ></div>
                <div
                    class="w-2 h-2 rounded-full transition-colors"
                    :class="step === 'code-input' ? 'bg-slate-800 dark:bg-white' : 'bg-slate-300 dark:bg-gray-600'"
                ></div>
                <div
                    class="w-2 h-2 rounded-full transition-colors"
                    :class="step === 'details' ? 'bg-slate-800 dark:bg-white' : 'bg-slate-300 dark:bg-gray-600'"
                ></div>
            </div>
        </div>
    </div>
</template>
